import { Form, InputNumber, Button, Space, message, Spin } from "antd"
import { useState, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { Marketplace, MarketplaceCode } from "../../../../domain/entities/Marketplace"

interface MarketplaceGiftCardFeeCardProps {
	marketplace?: Marketplace
	isLoading: boolean
	onSave: (code: MarketplaceCode, fee: number) => Promise<void>
}

const MarketplaceGiftCardFeeCard = ({ marketplace, isLoading, onSave }: MarketplaceGiftCardFeeCardProps) => {
	const { t } = useTranslation("marketplaces")
	const { t: tCommon } = useTranslation("common")
	const [form] = Form.useForm()
	const [editing, setEditing] = useState(false)
	const [saving, setSaving] = useState(false)

	useEffect(() => {
		if (marketplace) {
			form.setFieldsValue({ giftcardFee: marketplace.giftcardFee })
		}
	}, [marketplace])

	if (!marketplace) {
		return <Spin />
	}

	const handleEdit = () => {
		form.setFieldsValue({ giftcardFee: marketplace.giftcardFee })
		setEditing(true)
	}

	const handleCancel = () => {
		setEditing(false)
		form.resetFields()
	}

	const handleSave = async () => {
		try {
			const values = await form.validateFields()

			if (values.giftcardFee < 0 || values.giftcardFee > 100) {
				message.error(t("validations.feeInvalid"))
				return
			}

			setSaving(true)
			await onSave(marketplace.code, values.giftcardFee)
			setEditing(false)
			message.success(t("saveSuccess"))
		} catch (error) {
			message.error(t("saveError"))
		} finally {
			setSaving(false)
		}
	}

	return (
		<Spin spinning={isLoading || saving}>
			<div style={{ padding: "16px 24px" }}>
				<Form form={form}>
					<div style={{ marginBottom: 8, fontSize: 14 }}>
						<span style={{ color: "#ff4d4f", marginRight: 4 }}>*</span>
						{t("table.giftcardFee")}
					</div>
					<Space align="center">
						<Form.Item
							name="giftcardFee"
							noStyle
							rules={[{ required: true, message: t("validations.feeRequired") }]}
						>
							<InputNumber
								min={0}
								max={100}
								precision={2}
								addonAfter="%"
								disabled={!editing}
								style={{ width: 140 }}
							/>
						</Form.Item>
						{!editing ? (
							<Button type="primary" onClick={handleEdit}>
								{tCommon("edit")}
							</Button>
						) : (
							<>
								<Button type="primary" onClick={handleSave} loading={saving}>
									{tCommon("save")}
								</Button>
								<Button onClick={handleCancel} disabled={saving}>
									{tCommon("cancel")}
								</Button>
							</>
						)}
					</Space>
				</Form>
			</div>
		</Spin>
	)
}

export default MarketplaceGiftCardFeeCard
